/**
 * Rules customization hook.
 *
 * Persists the Customize-drawer state (sort field, sort direction,
 * page size, row density, column visibility, column order) to
 * localStorage so the user's preferences survive page reloads
 * and are remembered the next time they open the Rules page.
 *
 * State is keyed by tab (bank vs integration) so each tab
 * remembers its own column setup. A user can hide "Auto-post" on
 * the Bank rules tab while keeping it on Integration rules.
 *
 * localStorage access is wrapped in try/catch per Novala standing
 * rule: must render correctly in private browsing, SSR, and when
 * site storage is cleared or blocked.
 *
 * Backend swap: if customization moves server-side (per-user
 * preference stored in the account), replace loadFromStorage and
 * saveToStorage with API calls. Hook interface stays identical.
 */

import { useCallback, useEffect, useState } from "react";
import type {
  Rule,
  RuleTab,
  RulesCustomization,
  RuleColumnId,
} from "../pages/Accounting/Rules/rulesTypes";
import {
  DEFAULT_CUSTOMIZATION,
  DEFAULT_COLUMNS,
} from "../pages/Accounting/Rules/rulesTypes";

const STORAGE_KEY_PREFIX = "novala.rules.customization.";

function storageKey(tab: RuleTab): string {
  return `${STORAGE_KEY_PREFIX}${tab}`;
}

/**
 * Safe localStorage read. Returns null in private browsing, SSR,
 * blocked-storage, or malformed JSON.
 */
function loadFromStorage(tab: RuleTab): RulesCustomization | null {
  try {
    if (typeof window === "undefined") return null;
    const raw = window.localStorage.getItem(storageKey(tab));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as RulesCustomization;

    // Backfill any missing columns added in a later version of the
    // app so old saved state does not silently drop new columns.
    const savedIds = new Set(parsed.columns.map((c) => c.id));
    const missing = DEFAULT_COLUMNS.filter((c) => !savedIds.has(c.id));
    if (missing.length > 0) {
      parsed.columns = [...parsed.columns, ...missing];
    }

    // Drop any columns no longer in the DEFAULT list (removed).
    const defaultIds = new Set(DEFAULT_COLUMNS.map((c) => c.id));
    parsed.columns = parsed.columns.filter((c) => defaultIds.has(c.id));

    return parsed;
  } catch {
    return null;
  }
}

/** Safe localStorage write. Silently no-ops on failure. */
function saveToStorage(tab: RuleTab, value: RulesCustomization): void {
  try {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(storageKey(tab), JSON.stringify(value));
  } catch {
    // Private browsing or quota exceeded; preference just will not
    // survive a reload. Not a user-visible failure.
  }
}

/** Safe localStorage remove. Silently no-ops on failure. */
function clearStorage(tab: RuleTab): void {
  try {
    if (typeof window === "undefined") return;
    window.localStorage.removeItem(storageKey(tab));
  } catch {
    // Same reasoning as saveToStorage.
  }
}

export interface UseRulesCustomizationResult {
  customization: RulesCustomization;
  /** Patch any subset of customization fields. Persisted immediately. */
  update: (patch: Partial<RulesCustomization>) => void;
  /** Toggle a column's visibility by id. Persisted immediately. */
  toggleColumn: (columnId: RuleColumnId) => void;
  /** Reorder columns to the given id order. Persisted immediately. */
  reorderColumns: (orderedIds: RuleColumnId[]) => void;
  /** Reset to the default (clears saved state for this tab). */
  reset: () => void;
}

export function useRulesCustomization(tab: RuleTab): UseRulesCustomizationResult {
  const [customization, setCustomization] = useState<RulesCustomization>(
    () => loadFromStorage(tab) ?? DEFAULT_CUSTOMIZATION,
  );

  // Reload saved state when the tab changes.
  useEffect(() => {
    setCustomization(loadFromStorage(tab) ?? DEFAULT_CUSTOMIZATION);
  }, [tab]);

  const update = useCallback(
    (patch: Partial<RulesCustomization>) => {
      setCustomization((prev) => {
        const next = { ...prev, ...patch };
        saveToStorage(tab, next);
        return next;
      });
    },
    [tab],
  );

  const toggleColumn = useCallback(
    (columnId: RuleColumnId) => {
      setCustomization((prev) => {
        const next: RulesCustomization = {
          ...prev,
          columns: prev.columns.map((c) =>
            c.id === columnId ? { ...c, visible: !c.visible } : c,
          ),
        };
        saveToStorage(tab, next);
        return next;
      });
    },
    [tab],
  );

  const reorderColumns = useCallback(
    (orderedIds: RuleColumnId[]) => {
      setCustomization((prev) => {
        const byId = new Map(prev.columns.map((c) => [c.id, c]));
        const reordered = orderedIds
          .map((id) => byId.get(id))
          .filter((c): c is NonNullable<typeof c> => c !== undefined);

        // Append any columns not in orderedIds so nothing is lost.
        const seen = new Set(orderedIds);
        const remainder = prev.columns.filter((c) => !seen.has(c.id));

        const next: RulesCustomization = {
          ...prev,
          columns: [...reordered, ...remainder],
        };
        saveToStorage(tab, next);
        return next;
      });
    },
    [tab],
  );

  const reset = useCallback(() => {
    setCustomization(DEFAULT_CUSTOMIZATION);
    clearStorage(tab);
  }, [tab]);

  return { customization, update, toggleColumn, reorderColumns, reset };
}

/**
 * Return only visible columns in their current order. Used by the
 * table and card components to decide what to render.
 */
export function visibleColumns(customization: RulesCustomization) {
  return customization.columns.filter((c) => c.visible);
}

/** Avoid unused-import warning for the Rule type (surfaced via return type). */
export type _RuleTypeHint = Rule;